<template>
  <div class="pdv-tv-page" data-testid="pdv-tv-page">
    <div class="tv-header">
      <div class="tv-header-left">
        <i class="pi pi-desktop tv-header-icon"></i>
        <h1>{{ t('Painel de Produção') }}</h1>
      </div>
      <div class="tv-legend-inline">
        <span class="legend-item"><span class="legend-swatch legend-overdue"></span> {{ t('Atrasado') }}</span>
        <span class="legend-item"><span class="legend-swatch legend-today"></span> {{ t('Hoje') }}</span>
        <span class="legend-item"><span class="legend-swatch legend-tomorrow"></span> {{ t('Amanhã') }}</span>
        <span class="legend-item"><span class="legend-swatch legend-upcoming"></span> {{ t('Próximos') }}</span>
        <span class="legend-divider-v"></span>
        <span class="legend-item"><span class="legend-swatch legend-priority-high"></span> {{ t('Express') }}</span>
        <span class="legend-item"><span class="legend-swatch legend-priority-low"></span> {{ t('Padrão') }}</span>
      </div>
      <div class="tv-controls">
        <i class="pi pi-refresh tv-refresh-icon" @click="fetchData"></i>
        <span class="tv-updated">{{ t('Atualizado') }}: {{ lastUpdated }}</span>
        <span class="tv-clock">{{ currentTime }}</span>
      </div>
    </div>

    <!-- Page indicator -->
    <div class="tv-pages-indicator" v-if="pageCount > 1">
      <button
        class="tv-nav-btn"
        :disabled="currentPage === 0"
        @click="goToPage(currentPage - 1)"
        data-testid="tv-nav-prev"
      >
        <i class="pi pi-chevron-left"></i>
      </button>
      <span
        v-for="p in pageCount"
        :key="p"
        class="tv-page-dot"
        :class="{ active: p - 1 === currentPage }"
        @click="goToPage(p - 1)"
        :data-testid="`tv-page-dot-${p - 1}`"
      ></span>
      <button
        class="tv-nav-btn"
        :disabled="currentPage >= pageCount - 1"
        @click="goToPage(currentPage + 1)"
        data-testid="tv-nav-next"
      >
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>

    <!-- Columns grid -->
    <div class="tv-grid" data-testid="tv-grid">
      <div
        class="tv-column"
        v-for="stepGroup in visibleSteps"
        :key="stepOf(stepGroup).id"
        :data-testid="`tv-column-${stepOf(stepGroup).id}`"
      >
        <div class="section-header">
          <h2>{{ stepOf(stepGroup).name }}</h2>
          <span class="section-count">({{ stepGroup.items.length }})</span>
        </div>
        <div class="tv-cards">
          <div
            class="tv-card"
            :data-testid="`tv-card-${item.so_work_order_step_id}`"
            v-for="item in stepGroup.items"
            :key="item.so_work_order_step_id"
          >
            <!-- Urgency left border -->
            <div
              class="card-urgency-border"
              :class="`urgency-${getUrgency(item)}`"
            ></div>
            <div class="card-layout">
              <!-- Photo thumbnail -->
              <div class="card-photo" v-if="item.item_entry_photo_url">
                <img
                  :src="item.item_entry_photo_url"
                  :alt="item.so_order_item_description || item.cliente_nome"
                  class="card-photo-img"
                />
              </div>
              <div class="card-photo card-photo-empty" v-else>
                <i class="pi pi-image"></i>
              </div>
              <!-- Info -->
              <div class="card-info">
                <div class="card-client">
                  <i v-if="item.so_work_order_step_was_resumed" class="pi pi-refresh card-resumed-icon"></i>
                  {{ item.cliente_nome }}
                </div>
                <div class="card-model" v-if="item.so_order_item_description">
                  {{ item.so_order_item_description }}
                </div>
                <div class="card-meta">
                  <span
                    v-if="item.so_priority_name"
                    class="card-priority-badge"
                    :style="item.so_priority_color ? { backgroundColor: item.so_priority_color + '20', color: item.so_priority_color, borderColor: item.so_priority_color } : {}"
                  >
                    {{ item.so_priority_name }}
                  </span>
                  <span class="card-days" :class="urgencyDaysClass(item)">
                    {{ urgencyLabel(item) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            class="tv-card tv-card-empty"
            :data-testid="`tv-card-empty-${stepOf(stepGroup).id}`"
            v-if="stepGroup.items.length === 0"
          >
            <div class="card-empty-text">{{ t('Nenhum pendente') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="tv-loading" v-if="loading">
      <i class="pi pi-spin pi-spinner"></i> {{ t('Carregando...') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useSOTvDashboardBySteps } from '../../composables/useSOTvDashboardBySteps';
import type { Orders_TvDashboardByStepsItemRow } from '../../contracts/Orders_TvDashboardByStepsItemRow';
import type { Orders_TvDashboardByStepsResponseDados } from '../../contracts/Orders_TvDashboardByStepsResponse';
const { t } = useI18n();
const { getTvDashboardBySteps } = useSOTvDashboardBySteps();

// Use the typed steps shape from Orders_TvDashboardByStepsResponseDados directly —
// no `as` cast on the payload. The step field in the generated contract is
// shape-erased (object), so StepMeta pins the runtime shape for template use.
type StepGroup = Orders_TvDashboardByStepsResponseDados['steps'][number];
interface StepMeta { id: number; name: string; description: string | null; }
function stepOf(g: StepGroup): StepMeta { return g.step as unknown as StepMeta; }

const steps = ref<Orders_TvDashboardByStepsResponseDados['steps']>([]);
const loading = ref(true);
const lastUpdated = ref('');
const currentTime = ref('');
const currentPage = ref(0);

const COLS_PER_PAGE = 3 as const;
const ROTATION_INTERVAL_MS = 10000 as const;
const REFRESH_INTERVAL_MS = 60000 as const;

let refreshTimer: number | null = null;
let clockTimer: number | null = null;
let rotationTimer: number | null = null;

const pageCount = computed(() => {
  if (steps.value.length === 0) return 0;
  return Math.ceil(steps.value.length / COLS_PER_PAGE);
});

const visibleSteps = computed(() => {
  const start = currentPage.value * COLS_PER_PAGE;
  return steps.value.slice(start, start + COLS_PER_PAGE);
});

type Urgency = 'overdue' | 'today' | 'tomorrow' | 'upcoming';

function getUrgency(item: Orders_TvDashboardByStepsItemRow): Urgency {
  const d = item.days_until_due;
  if (d === null || d === undefined) return 'upcoming';
  if (d < 0) return 'overdue';
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  return 'upcoming';
}

function urgencyDaysClass(item: Orders_TvDashboardByStepsItemRow): string {
  return `days-${getUrgency(item)}`;
}

function urgencyLabel(item: Orders_TvDashboardByStepsItemRow): string {
  const d = item.days_until_due;
  if (d === null || d === undefined) return '';
  if (d < 0) return `${Math.abs(d)} ${t('dia(s) atrasado')}`;
  if (d === 0) return t('Entrega hoje');
  if (d === 1) return t('Amanhã');
  return `${d} ${t('dias')}`;
}

function goToPage(page: number): void {
  if (page < 0) page = pageCount.value - 1;
  if (page >= pageCount.value) page = 0;
  currentPage.value = page;
}

function advancePage(): void {
  if (pageCount.value <= 1) return;
  goToPage(currentPage.value + 1);
}

const updateClock = (): void => {
  const d = new Date();
  currentTime.value = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fetchData = async (): Promise<void> => {
  try {
    const dados = await getTvDashboardBySteps();
    if (dados) {
      steps.value = dados.steps;
    }
    lastUpdated.value = new Date().toLocaleTimeString('pt-BR');
  } catch (err) {
    log('PDVTVPage', 'fetchData', 'Error', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchData();
  updateClock();
  refreshTimer = window.setInterval(fetchData, REFRESH_INTERVAL_MS);
  clockTimer = window.setInterval(updateClock, 1000);
  rotationTimer = window.setInterval(advancePage, ROTATION_INTERVAL_MS);

  // Refresh on demand when production steps are scanned/advanced
  eventBus.on('pdv.producao.refresh', fetchData);
});

onBeforeUnmount(() => {
  if (refreshTimer) clearInterval(refreshTimer);
  if (clockTimer) clearInterval(clockTimer);
  if (rotationTimer) clearInterval(rotationTimer);

  eventBus.off('pdv.producao.refresh', fetchData);
});
</script>

<style scoped>
.pdv-tv-page {
  padding: 10px;
  min-height: calc(100vh - 60px);
  background: var(--p-surface-50, #fafafa);
}

.tv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  padding: 8px 14px;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
}

.tv-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tv-header-icon {
  font-size: 1.5rem;
  color: var(--p-primary-color);
}

.tv-header h1 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--p-text-color, #333);
}

/* Inline legend inside header */
.tv-legend-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--p-text-color, #555);
  white-space: nowrap;
}

.legend-swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  border: 1px solid rgba(0,0,0,0.1);
}

.legend-overdue { background: var(--p-red-500, #ef4444); }
.legend-today   { background: var(--p-orange-500, #f97316); }
.legend-tomorrow { background: var(--p-blue-500, #3b82f6); }
.legend-upcoming { background: var(--p-green-500, #22c55e); }
.legend-priority-high { background: #EF4444; }
.legend-priority-low  { background: #6B7280; }

.legend-divider-v {
  display: inline-block;
  width: 1px;
  height: 14px;
  background: var(--p-surface-200, #e5e7eb);
}

.tv-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tv-refresh-icon {
  font-size: 0.95rem;
  color: var(--p-primary-color);
  cursor: pointer;
  transition: transform 0.2s;
}
.tv-refresh-icon:hover { transform: rotate(90deg); }

.tv-updated {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #888);
}

.tv-clock {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-primary-color);
  font-family: 'Courier New', monospace;
  background: var(--p-surface-100, #f3f4f6);
  padding: 3px 10px;
  border-radius: 5px;
}

/* Page indicator / navigation */
.tv-pages-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.tv-nav-btn {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  color: var(--p-primary-color);
  font-size: 1rem;
  transition: background 0.2s;
}
.tv-nav-btn:hover:not(:disabled) { background: var(--p-surface-100, #f3f4f6); }
.tv-nav-btn:disabled { opacity: 0.3; cursor: default; }

.tv-page-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--p-surface-300, #d1d5db);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}
.tv-page-dot.active {
  background: var(--p-primary-color);
  transform: scale(1.3);
}

/* Columns grid */
.tv-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

@media (max-width: 900px) {
  .tv-grid { grid-template-columns: 1fr; }
}

.tv-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.section-header h2 {
  margin: 0;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 1.3rem;
  font-weight: 700;
  display: inline-block;
  background: var(--p-primary-100, #dbeafe);
  color: var(--p-primary-700, #1d4ed8);
}

.section-count {
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #888);
}

.tv-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tv-card {
  position: relative;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 10px;
  min-height: 120px;
}

/* Urgency left border */
.card-urgency-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  border-radius: 12px 0 0 12px;
}
.urgency-overdue { background: var(--p-red-500, #ef4444); }
.urgency-today   { background: var(--p-orange-500, #f97316); }
.urgency-tomorrow { background: var(--p-blue-500, #3b82f6); }
.urgency-upcoming { background: var(--p-green-500, #22c55e); }

.tv-card-empty {
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

/* Card layout: photo + info side by side */
.card-layout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.card-photo {
  flex-shrink: 0;
  width: 150px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--p-surface-100, #f3f4f6);
  border: 2px solid var(--p-surface-200, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-photo-empty {
  color: var(--p-text-muted-color, #bbb);
  font-size: 3rem;
}

.card-photo-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  min-height: 150px;
}

.card-client {
  font-weight: 700;
  font-size: 1.4rem;
  color: var(--p-text-color, #333);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-resumed-icon {
  color: var(--p-orange-500, #f97316);
  font-size: 0.8em;
  margin-right: 4px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.card-model {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--p-text-color, #111);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.card-priority-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-days {
  margin-top: 6px;
  font-size: 1.15rem;
  font-weight: 700;
}

.days-overdue { color: var(--p-red-600, #dc2626); }
.days-today   { color: var(--p-orange-600, #ea580c); }
.days-tomorrow { color: var(--p-blue-600, #2563eb); }
.days-upcoming { color: var(--p-green-600, #16a34a); }

.card-empty-text {
  color: var(--p-text-muted-color, #bbb);
  font-size: 0.85rem;
  font-style: italic;
}

.tv-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px;
  color: var(--p-text-muted-color, #888);
  font-size: 1rem;
}

</style>
